import type { Dispatch } from "react";
import type { EditorAction } from "../editor/reducers";
import type { TransformableRegionFragment } from "../client/generated";
import { useState } from "react";
import { useOverlayScale, usePageScale } from "../editor/hooks";
import { toPercent, CSS_IN_TO_PX } from "../editor/utils";
import { RegionTransformHandle } from "./RegionTransformHandle";
import { RegionPolygon } from "./RegionPolygon";
import { RegionMoveHandler } from "./RegionMoveHandler";
import { RegionSelectionHandler } from "./RegionSelectionHandler";
import { RegionShortcutsHandler } from "./RegionShortcutsHandler";
import { RegionLabel } from "./RegionLabel";
import { RegionCanvas } from "./RegionCanvas";
import { RegionContextMenuContent } from "./RegionContextMenuContent";
import { cn } from "@acdc2/ui/lib/utils";
import { useUpdateRegionShapeMutation } from "../client/generated";
import {
  ContextMenu,
  ContextMenuTrigger,
} from "@acdc2/ui/components/context-menu";

type Props = {
  fragment: TransformableRegionFragment;
  isSelected: boolean;
  dispatch: Dispatch<EditorAction>;
};

export function TransformableRegion({ fragment, isSelected, dispatch }: Props) {
  const overlayScale = useOverlayScale();
  const pageScale = usePageScale();
  const pageWidth = fragment.page.width * CSS_IN_TO_PX;
  const pageHeight = fragment.page.height * CSS_IN_TO_PX;
  const [aabb, setAabb] = useState(fragment.shape.aabb);

  const transformVertexX = (x: number) => {
    const relativeVertexX =
      (x - fragment.shape.aabb.left) / fragment.shape.aabb.width;
    return aabb.left + relativeVertexX * aabb.width;
  };

  const transformVertexY = (y: number) => {
    const relativeY =
      (y - fragment.shape.aabb.top) / fragment.shape.aabb.height;
    return aabb.top + relativeY * aabb.height;
  };

  const vertices = fragment.shape.vertices.map((vertex) => ({
    left: transformVertexX(vertex.left),
    top: transformVertexY(vertex.top),
  }));

  const [updateRegionShape] = useUpdateRegionShapeMutation();

  const onDone = async () => {
    const verticesInCounterclockwiseOrder =
      aabb.width * aabb.height > 0 ? vertices : [...vertices].reverse();

    const { errors, data } = await updateRegionShape({
      variables: {
        regionId: fragment.id,
        vertices: verticesInCounterclockwiseOrder.map((vertex) => ({
          left: vertex.left,
          top: vertex.top,
        })),
      },
    });

    if (errors) {
      console.error("Error moving region:", errors);
    } else if (data?.updateRegionShape.userErrors) {
      console.error(
        "User error moving region:",
        data.updateRegionShape.userErrors,
      );
    }

    setAabb(data?.updateRegionShape.region?.shape.aabb ?? fragment.shape.aabb);
  };

  return (
    <ContextMenu>
      <RegionContextMenuContent dispatch={dispatch} fragment={fragment} />
      <RegionCanvas className={cn({ "z-50": isSelected })}>
        <ContextMenuTrigger asChild>
          <RegionMoveHandler
            isSelected={isSelected}
            onDone={onDone}
            onMove={(movementX, movementY) => {
              setAabb((oldAabb) => ({
                ...oldAabb,
                top: oldAabb.top + movementY / pageHeight / pageScale,
                left: oldAabb.left + movementX / pageWidth / pageScale,
              }));
            }}
          >
            <RegionShortcutsHandler fragment={fragment} dispatch={dispatch}>
              <RegionSelectionHandler fragment={fragment} dispatch={dispatch}>
                <RegionPolygon
                  fragment={{
                    ...fragment,
                    shape: { ...fragment.shape, vertices },
                  }}
                />
              </RegionSelectionHandler>
            </RegionShortcutsHandler>
          </RegionMoveHandler>
        </ContextMenuTrigger>

        {isSelected && (
          <>
            <rect
              x={toPercent(Math.min(aabb.left, aabb.left + aabb.width))}
              y={toPercent(Math.min(aabb.top, aabb.top + aabb.height))}
              width={toPercent(Math.abs(aabb.width))}
              height={toPercent(Math.abs(aabb.height))}
              fill="transparent"
              stroke="var(--color-brand)"
              strokeWidth={2 / overlayScale}
              className="pointer-events-none"
            />

            <RegionTransformHandle
              direction="horizontal"
              cx={aabb.left * pageWidth}
              cy={(aabb.top + aabb.height / 2) * pageHeight}
              onDone={onDone}
              onMove={(movementX) => {
                setAabb((oldAabb) => ({
                  ...oldAabb,
                  left: oldAabb.left + movementX / pageWidth / pageScale,
                  width: oldAabb.width - movementX / pageWidth / pageScale,
                }));
              }}
            />
            <RegionTransformHandle
              direction="horizontal"
              cx={(aabb.left + aabb.width) * pageWidth}
              cy={(aabb.top + aabb.height / 2) * pageHeight}
              onDone={onDone}
              onMove={(movementX) => {
                setAabb((oldAabb) => ({
                  ...oldAabb,
                  width: oldAabb.width + movementX / pageWidth / pageScale,
                }));
              }}
            />

            <RegionTransformHandle
              direction="vertical"
              cx={(aabb.left + aabb.width / 2) * pageWidth}
              cy={aabb.top * pageHeight}
              onDone={onDone}
              onMove={(_, movementY) => {
                setAabb((oldAabb) => ({
                  ...oldAabb,
                  top: oldAabb.top + movementY / pageHeight / pageScale,
                  height: oldAabb.height - movementY / pageHeight / pageScale,
                }));
              }}
            />
            <RegionTransformHandle
              direction="vertical"
              cx={(aabb.left + aabb.width / 2) * pageWidth}
              cy={(aabb.top + aabb.height) * pageHeight}
              onDone={onDone}
              onMove={(_, movementY) => {
                setAabb((oldAabb) => ({
                  ...oldAabb,
                  height: oldAabb.height + movementY / pageHeight / pageScale,
                }));
              }}
            />

            <RegionTransformHandle
              direction="primary"
              cx={aabb.left * pageWidth}
              cy={(aabb.top + aabb.height) * pageHeight}
              onDone={onDone}
              onMove={(movementX, movementY) => {
                setAabb((oldAabb) => ({
                  ...oldAabb,
                  left: oldAabb.left + movementX / pageWidth / pageScale,
                  width: oldAabb.width - movementX / pageWidth / pageScale,
                  height: oldAabb.height + movementY / pageHeight / pageScale,
                }));
              }}
            />
            <RegionTransformHandle
              direction="primary"
              cx={(aabb.left + aabb.width) * pageWidth}
              cy={aabb.top * pageHeight}
              onDone={onDone}
              onMove={(movementX, movementY) => {
                setAabb((oldAabb) => ({
                  ...oldAabb,
                  top: oldAabb.top + movementY / pageHeight / pageScale,
                  width: oldAabb.width + movementX / pageWidth / pageScale,
                  height: oldAabb.height - movementY / pageHeight / pageScale,
                }));
              }}
            />

            <RegionTransformHandle
              direction="secondary"
              cx={aabb.left * pageWidth}
              cy={aabb.top * pageHeight}
              onDone={onDone}
              onMove={(movementX, movementY) => {
                setAabb((oldAabb) => ({
                  top: oldAabb.top + movementY / pageHeight / pageScale,
                  left: oldAabb.left + movementX / pageWidth / pageScale,
                  width: oldAabb.width - movementX / pageWidth / pageScale,
                  height: oldAabb.height - movementY / pageHeight / pageScale,
                }));
              }}
            />
            <RegionTransformHandle
              direction="secondary"
              cx={(aabb.left + aabb.width) * pageWidth}
              cy={(aabb.top + aabb.height) * pageHeight}
              onDone={onDone}
              onMove={(movementX, movementY) => {
                setAabb((oldAabb) => ({
                  ...oldAabb,
                  width: oldAabb.width + movementX / pageWidth / pageScale,
                  height: oldAabb.height + movementY / pageHeight / pageScale,
                }));
              }}
            />
          </>
        )}

        {!isSelected && (
          <RegionLabel
            fragment={{ ...fragment, shape: { ...fragment.shape, vertices } }}
          />
        )}
      </RegionCanvas>
    </ContextMenu>
  );
}
